import { Url } from '../types';
import { supabase, getSupabaseClient } from './supabase';

const PAGE_SIZE = 1000;

/**
 * Page through a Supabase query past PostgREST's 1000-row default cap by repeatedly
 * calling `fetchPage(from, to)` with `.range()` bounds until a short page (or an error)
 * ends the loop. Shared helper (WD-3622) for every url-fetching caller that used to
 * silently truncate at 1000 rows (nuevayork.es has 1,341 URLs, newyork.kr 1,258,
 * newyork.nl 1,142).
 */
export async function paginateAll<T>(
  fetchPage: (from: number, to: number) => Promise<{ data: T[] | null; error: { message: string } | null }>,
  pageSize: number = PAGE_SIZE
): Promise<{ rows: T[]; error: string | null }> {
  const rows: T[] = [];
  let from = 0;
  // Hard stop at 1000 pages so a bug in fetchPage can't spin forever.
  for (let page = 0; page < 1000; page++) {
    const { data, error } = await fetchPage(from, from + pageSize - 1);
    if (error) {
      return { rows, error: error.message };
    }
    if (!data || data.length === 0) break;
    rows.push(...data);
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return { rows, error: null };
}

export const urlService = {
  async getUrlsByWebsite(websiteId: string): Promise<{ urls: Url[]; error: string | null }> {
    try {
      console.log('Fetching URLs for website_id:', websiteId);

      // Use admin client to bypass RLS; paginate past the 1000-row PostgREST cap.
      const { rows, error } = await paginateAll<Url>(async (from, to) => {
        const { data, error } = await getSupabaseClient(true)
          .from('urls')
          .select('*')
          .eq('website_id', websiteId)
          .order('url_id')
          .range(from, to);
        return { data, error };
      });

      if (error) {
        console.error('Error fetching URLs:', error);
        return { urls: [], error };
      }

      console.log('Fetched URLs:', rows.length);

      return { urls: rows, error: null };
    } catch (error: any) {
      console.error('Exception fetching URLs:', error);
      return { urls: [], error: error.message };
    }
  },

  // Same pagination, with no website filter — for callers that list URLs across every site.
  async getAllUrls(): Promise<{ urls: Url[]; error: string | null }> {
    try {
      const { rows, error } = await paginateAll<Url>(async (from, to) => {
        const { data, error } = await getSupabaseClient(true)
          .from('urls')
          .select('*')
          .order('url_id')
          .range(from, to);
        return { data, error };
      });

      if (error) {
        console.error('Error fetching URLs:', error);
        return { urls: [], error };
      }

      return { urls: rows, error: null };
    } catch (error: any) {
      console.error('Exception fetching URLs:', error);
      return { urls: [], error: error.message };
    }
  },

  async getUrl(urlId: string): Promise<{ url: Url | null; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('urls')
        .select('*')
        .eq('url_id', urlId)
        .single();

      if (error) {
        return { url: null, error: error.message };
      }

      return { url: data, error: null };
    } catch (error: any) {
      return { url: null, error: error.message };
    }
  },

  async createUrl(
    urlData: { url_id: string; full_url: string; website_id: string; title?: string; path?: string }
  ): Promise<{ url: Url | null; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('urls')
        .insert([
          {
            url_id: urlData.url_id,
            full_url: urlData.full_url,
            website_id: urlData.website_id,
            title: urlData.title,
            path: urlData.path,
          },
        ])
        .select()
        .single();

      if (error) {
        return { url: null, error: error.message };
      }

      return { url: data, error: null };
    } catch (error: any) {
      return { url: null, error: error.message };
    }
  },

  async updateUrl(
    urlId: string,
    urlData: { full_url?: string; title?: string; path?: string; meta_description?: string }
  ): Promise<{ url: Url | null; error: string | null }> {
    try {
      const updateData: any = {};
      if (urlData.full_url !== undefined) updateData.full_url = urlData.full_url;
      if (urlData.title !== undefined) updateData.title = urlData.title;
      if (urlData.path !== undefined) updateData.path = urlData.path;
      if (urlData.meta_description !== undefined) updateData.meta_description = urlData.meta_description;
      
      const { data, error } = await supabase
        .from('urls')
        .update(updateData)
        .eq('url_id', urlId)
        .select()
        .single();

      if (error) {
        return { url: null, error: error.message };
      }

      return { url: data, error: null };
    } catch (error: any) {
      return { url: null, error: error.message };
    }
  },

  async deleteUrl(urlId: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.from('urls').delete().eq('url_id', urlId);
      if (error) return { error: error.message };
      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  },

  async bulkCreateUrls(
    urls: Array<{ url_id: string; full_url: string; website_id: string; title?: string; path?: string; meta_description?: string }>
  ): Promise<{ count: number; error: string | null }> {
    try {
      const insertData = urls.map((u) => ({
        url_id: u.url_id,
        full_url: u.full_url,
        website_id: u.website_id,
        title: u.title || null,
        path: u.path || null,
        meta_description: u.meta_description || null,
      }));

      const { data, error } = await supabase.from('urls').insert(insertData as any).select();

      if (error) {
        return { count: 0, error: error.message };
      }

      return { count: data.length, error: null };
    } catch (error: any) {
      return { count: 0, error: error.message };
    }
  },
};